/**
 * LINE ログインの窓口（doc/AUTH.md §21）。firebase SDK は import しない（初期バンドルに乗るため）。
 *
 * サーバの `/v1/auth/line/start` を開くと、LINE で入ったあとに `/v1/auth/line/callback` が Firebase の
 * カスタムトークンを返してくる。受け取り方は 2 つ。
 *   - ポップアップ（ふだん）: 戻りの画面が、開いた窓へ postMessage と BroadcastChannel で渡す
 *     （LINE の画面を経ると窓の繋がり `window.opener` が切れることがあるので、両方で待つ）
 *   - リダイレクト（ポップアップが開けないとき）: 戻りの画面が sessionStorage に置いて元のページへ戻す。
 *     AuthProvider が起動時に `takeLineRedirectResult()` で拾う
 */

export interface LineGrant {
  readonly customToken: string;
  readonly uid: string;
  readonly name: string | null;
}

export class LineLoginError extends Error {
  constructor(readonly code: 'popup-blocked' | 'cancelled' | 'expired' | 'failed' | 'unavailable', message: string) {
    super(message);
    this.name = 'LineLoginError';
  }
}

interface LineMessage {
  readonly type: 'gendan-line-login';
  readonly rid: string;
  readonly ok: boolean;
  readonly customToken?: string;
  readonly uid?: string;
  readonly name?: string | null;
  readonly error?: LineLoginError['code'];
  readonly message?: string;
}

const CHANNEL = 'gendan-line-login';
const REDIRECT_KEY = 'gendan.lineLogin';
/** 窓が閉じたように見えても、しばらくは結果を待つ（窓の繋がりが切れると、開いている最中でも閉じたように見える）。 */
const LATE_WINDOW_MS = 10 * 60 * 1000;

const apiBase = (import.meta.env.VITE_API_BASE_URL ?? '').trim().replace(/\/+$/, '') || '/v1';

const newRid = (): string => {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

const here = (): string => `${window.location.pathname}${window.location.search}`;

const startUrl = (mode: 'popup' | 'redirect', rid: string): string =>
  `${apiBase}/auth/line/start?${new URLSearchParams({ mode, rid, return: here() }).toString()}`;

const isMessage = (value: unknown): value is LineMessage =>
  typeof value === 'object' && value !== null && (value as { type?: unknown }).type === 'gendan-line-login';

function toGrant(message: LineMessage): LineGrant {
  if (!message.ok || !message.customToken || !message.uid) {
    throw new LineLoginError(message.error ?? 'failed', message.message ?? 'LINE でのログインに失敗しました。もう一度お試しください。');
  }
  return { customToken: message.customToken, uid: message.uid, name: message.name ?? null };
}

/**
 * ポップアップで LINE に入ってもらい、カスタムトークンを受け取る。
 * 開けなければ `popup-blocked`。閉じられたら `cancelled` で断るが、そのあとに結果が届いたら `onLate` に渡す。
 */
export function requestLineGrant(onLate?: (grant: LineGrant) => void): Promise<LineGrant> {
  const rid = newRid();
  const popup = window.open(startUrl('popup', rid), 'gendan-line-login', 'popup,width=480,height=720');
  if (!popup) {
    return Promise.reject(new LineLoginError('popup-blocked', 'ブラウザがポップアップを止めました。ポップアップを許可してからもう一度お試しください。'));
  }

  return new Promise<LineGrant>((resolve, reject) => {
    let settled = false;
    let channel: BroadcastChannel | null = null;
    const cleanup = () => {
      window.removeEventListener('message', onWindowMessage);
      channel?.close();
      window.clearInterval(watcher);
      window.clearTimeout(expiry);
    };
    const receive = (data: unknown) => {
      if (!isMessage(data) || data.rid !== rid) return;
      cleanup();
      try { popup.close(); } catch { /* 繋がりが切れていれば閉じられない。戻りの画面が自分で閉じる */ }
      try {
        const grant = toGrant(data);
        if (settled) onLate?.(grant);
        else resolve(grant);
      } catch (error) {
        if (!settled) reject(error);
      }
      settled = true;
    };
    const onWindowMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      receive(event.data);
    };
    window.addEventListener('message', onWindowMessage);
    try {
      channel = new BroadcastChannel(CHANNEL);
      channel.onmessage = (event) => receive(event.data);
    } catch { /* BroadcastChannel の無いブラウザは postMessage だけで待つ */ }

    const watcher = window.setInterval(() => {
      if (settled || !popup.closed) return;
      // 閉じられた（か、繋がりが切れた）。呼び手には取りやめを返すが、届いたら onLate で入れる
      settled = true;
      window.clearInterval(watcher);
      reject(new LineLoginError('cancelled', 'ログインが途中で閉じられました。'));
    }, 600);
    const expiry = window.setTimeout(() => {
      cleanup();
      if (!settled) reject(new LineLoginError('cancelled', 'ログインが途中で閉じられました。'));
      settled = true;
    }, LATE_WINDOW_MS);
  });
}

/** ページごと LINE へ移る（ポップアップが開けないとき）。戻ると `takeLineRedirectResult()` で拾う。 */
export function startLineRedirect(): void {
  window.location.assign(startUrl('redirect', newRid()));
}

/**
 * リダイレクトで戻ってきた結果を 1 度だけ取り出す（取り出したら消す）。無ければ null。
 * 失敗の結果なら `LineLoginError` を返す（画面はログインのダイアログにその文言を出す）。
 */
export function takeLineRedirectResult(): LineGrant | LineLoginError | null {
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(REDIRECT_KEY);
    if (raw) window.sessionStorage.removeItem(REDIRECT_KEY);
  } catch { return null; }
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isMessage(parsed)) return null;
    return toGrant(parsed);
  } catch (error) {
    return error instanceof LineLoginError ? error : null;
  }
}
